import React, { useState } from 'react';
import { Save, Shield, Bell, Globe, Palette, Users } from 'lucide-react';
import { APP_CONFIG } from '../../config/app.config';
import { useAppStore } from '../../store/useAppStore';

const tabs = [
  { id: 'company', label: 'Company Info', icon: Globe },
  { id: 'branding', label: 'Branding', icon: Palette },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'roles', label: 'Roles & Permissions', icon: Users },
];

const AdminSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState('company');
  const { theme, setTheme } = useAppStore();
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontWeight: 800, fontSize: '1.5rem', color: 'var(--text-primary)', margin: 0 }}>Settings</h2>
        <button onClick={handleSave} className="btn-primary" style={{ fontSize: '0.875rem' }}>
          <Save size={16} /> {saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '1.5rem', alignItems: 'start' }}>
        {/* Tab Nav */}
        <div className="card" style={{ padding: '0.5rem' }}>
          {tabs.map(t => {
            const TabIcon = t.icon;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.625rem 0.875rem', borderRadius: '0.5rem',
                border: 'none', cursor: 'pointer', fontSize: '0.875rem', fontWeight: activeTab === t.id ? 600 : 400,
                background: activeTab === t.id ? 'rgb(249 115 22 / 0.1)' : 'transparent',
                color: activeTab === t.id ? '#f97316' : 'var(--text-secondary)',
                transition: 'all 0.15s', textAlign: 'left', marginBottom: '0.2rem',
              }}>
                <TabIcon size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="card">
          {activeTab === 'company' && (
            <div>
              <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Company Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                {[
                  { label: 'Company Name', value: APP_CONFIG.name, full: true },
                  { label: 'Phone Number', value: APP_CONFIG.phone },
                  { label: 'Email Address', value: APP_CONFIG.email },
                  { label: 'Street Address', value: APP_CONFIG.address, full: true },
                  { label: 'City', value: APP_CONFIG.city },
                  { label: 'State', value: APP_CONFIG.state },
                  { label: 'ZIP Code', value: APP_CONFIG.zip },
                  { label: 'License Number', value: APP_CONFIG.licenses[0] },
                ].map(field => (
                  <div key={field.label} style={{ gridColumn: field.full ? '1 / -1' : undefined }}>
                    <label className="label">{field.label}</label>
                    <input className="input-field" defaultValue={field.value} />
                  </div>
                ))}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="label">Company Description</label>
                  <textarea className="input-field" defaultValue={APP_CONFIG.description} rows={3} style={{ resize: 'vertical' }} />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'branding' && (
            <div>
              <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Branding & Theme</h3>
              <div style={{ marginBottom: '1.5rem' }}>
                <label className="label">Color Theme</label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                  {['light', 'dark'].map(t => (
                    <button key={t} onClick={() => setTheme(t as any)} style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1.5rem', borderRadius: '0.75rem',
                      border: `2px solid ${theme === t ? '#f97316' : 'var(--border-color)'}`,
                      background: t === 'dark' ? '#0f172a' : '#ffffff',
                      cursor: 'pointer', transition: 'all 0.2s',
                    }}>
                      <span style={{ color: t === 'dark' ? 'white' : '#0f172a', fontWeight: 600, textTransform: 'capitalize', fontSize: '0.875rem' }}>{t} Mode</span>
                      {theme === t && <span style={{ color: '#f97316', fontSize: '0.8rem', fontWeight: 700 }}>Active</span>}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">Primary Brand Color</label>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                  {['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#14b8a6'].map(color => (
                    <div key={color} style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', background: color, cursor: 'pointer', border: color === '#f97316' ? '3px solid var(--text-primary)' : '3px solid transparent', transition: 'transform 0.2s' }}
                      onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.15)')}
                      onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div>
              <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Notification Preferences</h3>
              {[
                { label: 'New Lead Notification', desc: 'Get notified when a new lead is submitted', checked: true },
                { label: 'Quote Accepted', desc: 'When a customer accepts a quote', checked: true },
                { label: 'New Appointment', desc: 'When an appointment is scheduled', checked: true },
                { label: 'Invoice Overdue', desc: 'When an invoice becomes overdue', checked: true },
                { label: 'Project Completed', desc: 'When a contractor marks a job complete', checked: false },
                { label: 'Weekly Summary', desc: 'Weekly digest of business activity', checked: false },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 0', borderBottom: '1px solid var(--border-color)' }}>
                  <div>
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0, fontSize: '0.9rem' }}>{item.label}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0.2rem 0 0' }}>{item.desc}</p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', position: 'relative' }}>
                    <input type="checkbox" defaultChecked={item.checked} style={{ width: '2.5rem', height: '1.25rem', cursor: 'pointer', accentColor: '#f97316' }} />
                  </label>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'security' && (
            <div>
              <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Security Settings</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {[
                  { label: 'Two-Factor Authentication', desc: 'Require 2FA for all admin logins', checked: false },
                  { label: 'Session Timeout', desc: 'Automatically log out after 4 hours of inactivity', checked: true },
                  { label: 'Login Notifications', desc: 'Email alert on new login from new device', checked: true },
                  { label: 'IP Allowlist', desc: 'Restrict admin access to specific IP addresses', checked: false },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.2rem', fontSize: '0.9rem' }}>{item.label}</p>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: 0 }}>{item.desc}</p>
                    </div>
                    <input type="checkbox" defaultChecked={item.checked} style={{ accentColor: '#f97316', width: '1.1rem', height: '1.1rem', cursor: 'pointer' }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'roles' && (
            <div>
              <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Roles & Permissions</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { role: 'Super Admin', desc: 'Full system access. Can manage all settings, users, and data.', color: '#fee2e2', textColor: '#991b1b' },
                  { role: 'Admin', desc: 'Can manage leads, projects, appointments, and reports.', color: '#fef9c3', textColor: '#854d0e' },
                  { role: 'Seller', desc: 'Can view and manage leads and quotes assigned to them.', color: '#f5f3ff', textColor: '#7e22ce' },
                  { role: 'Contractor', desc: 'Can view assigned jobs, update progress, and submit reports.', color: '#dcfce7', textColor: '#166534' },
                  { role: 'Customer', desc: 'Can view their own projects, quotes, and invoices.', color: '#dbeafe', textColor: '#1e40af' },
                ].map(r => (
                  <div key={r.role} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: r.color, borderRadius: '0.75rem' }}>
                    <div style={{ padding: '0.25rem 0.75rem', background: r.textColor, color: 'white', borderRadius: '0.375rem', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 }}>{r.role}</div>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: r.textColor, flex: 1 }}>{r.desc}</p>
                    <button style={{ background: 'none', border: `1px solid ${r.textColor}`, color: r.textColor, borderRadius: '0.5rem', padding: '0.375rem 0.75rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Edit</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@media(max-width:768px){div[style*="grid-template-columns: 200px 1fr"]{grid-template-columns:1fr!important}}`}</style>
    </div>
  );
};

export default AdminSettings;
